import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';

function App() {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [movieTitle, setMovieTitle] = useState('');
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_fontSize');
    return saved ? parseInt(saved) : 140;
  });
  const [selectedFont, setSelectedFont] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_selectedFont');
    return saved || 'Bebas Neue';
  });
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [backgroundPosition, setBackgroundPosition] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_backgroundPosition');
    return saved ? parseInt(saved) : 50;
  });
  const [textAlignHorizontal, setTextAlignHorizontal] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_textAlignHorizontal');
    return saved || 'left';
  });
  const [textAlignVertical, setTextAlignVertical] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_textAlignVertical');
    return saved || 'bottom';
  });
  const [textPositionX, setTextPositionX] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_textPositionX');
    return saved ? parseInt(saved) : 6.25; // 120px / 1920px = 6.25%
  });
  const [textPositionY, setTextPositionY] = useState(() => {
    const saved = localStorage.getItem('thumbMaker_textPositionY');
    return saved ? parseInt(saved) : 13.89; // 150px / 1080px = 13.89%
  });
  const [previewScale, setPreviewScale] = useState(1);
  const [previewBlob, setPreviewBlob] = useState(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const watermarkInputRef = useRef(null);
  const previewRef = useRef(null);

  // Lista de fontes ideais para thumbnails
  const fontOptions = [
    { name: 'Bebas Neue', family: '"Bebas Neue", sans-serif' },
    { name: 'Anton', family: '"Anton", sans-serif' },
    { name: 'Oswald', family: '"Oswald", sans-serif' },
    { name: 'Roboto Condensed', family: '"Roboto Condensed", sans-serif' },
    { name: 'Barlow Condensed', family: '"Barlow Condensed", sans-serif' },
    { name: 'Fjalla One', family: '"Fjalla One", sans-serif' },
    { name: 'Russo One', family: '"Russo One", sans-serif' },
    { name: 'Archivo Black', family: '"Archivo Black", sans-serif' },
    { name: 'Impact', family: '"Impact", sans-serif' },
    { name: 'Helvetica Neue', family: '"Helvetica Neue", "Arial Black", sans-serif' }
  ];

  const getCurrentFontFamily = () => {
    const font = fontOptions.find(f => f.name === selectedFont);
    return font ? font.family : fontOptions[0].family;
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWatermarkUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setWatermarkImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Salvar configurações no localStorage sempre que mudarem
  useEffect(() => {
    localStorage.setItem('thumbMaker_fontSize', fontSize.toString());
    localStorage.setItem('thumbMaker_selectedFont', selectedFont);
    localStorage.setItem('thumbMaker_backgroundPosition', backgroundPosition.toString());
    localStorage.setItem('thumbMaker_textAlignHorizontal', textAlignHorizontal);
    localStorage.setItem('thumbMaker_textAlignVertical', textAlignVertical);
    localStorage.setItem('thumbMaker_textPositionX', textPositionX.toString());
    localStorage.setItem('thumbMaker_textPositionY', textPositionY.toString());
  }, [fontSize, selectedFont, backgroundPosition, textAlignHorizontal, textAlignVertical, textPositionX, textPositionY]);

  // Calcular escala do preview baseado na largura real
  useEffect(() => {
    const updatePreviewScale = () => {
      if (previewRef.current) {
        const previewWidth = previewRef.current.offsetWidth;
        const scale = previewWidth / 1920; // 1920 é a largura final da imagem
        setPreviewScale(scale);
      }
    };

    // Usar setTimeout para garantir que o DOM esteja totalmente renderizado
    const timeoutId = setTimeout(updatePreviewScale, 100);
    
    window.addEventListener('resize', updatePreviewScale);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePreviewScale);
    };
  }, [backgroundImage, movieTitle]);

  // Recalcular quando a imagem carregar
  const handleImageLoad = () => {
    setTimeout(() => {
      if (previewRef.current) {
        const previewWidth = previewRef.current.offsetWidth;
        const scale = previewWidth / 1920;
        setPreviewScale(scale);
      }
    }, 50);
  };

  // Gerar preview como blob
  const generatePreviewBlob = async () => {
    if (!backgroundImage || !movieTitle) {
      setPreviewBlob(null);
      return;
    }

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Usar dimensões do preview para manter proporção
    const previewWidth = previewRef.current?.offsetWidth || 800;
    const previewHeight = previewWidth * (9/16); // 16:9
    
    canvas.width = previewWidth;
    canvas.height = previewHeight;

    const img = new Image();
    img.onload = () => {
      // Desenhar imagem de fundo com posição ajustável
      const imgAspect = img.width / img.height;
      const canvasAspect = canvas.width / canvas.height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgAspect > canvasAspect) {
        // Imagem mais larga - ajustar altura
        drawHeight = canvas.height;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvas.width - drawWidth) / 2;
        drawY = 0;
      } else {
        // Imagem mais alta - ajustar largura e posição vertical
        drawWidth = canvas.width;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        // Aplicar posição vertical
        const maxOffset = drawHeight - canvas.height;
        drawY = -(maxOffset * backgroundPosition / 100);
      }
      
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      
      // Criar gradiente para sombra na parte inferior
      const gradient = ctx.createLinearGradient(0, canvas.height * 0.5, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
      
      // Aplicar sombra
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Configurar texto
      ctx.fillStyle = 'white';
      ctx.textAlign = textAlignHorizontal;
      ctx.textBaseline = 'alphabetic'; // Usar baseline consistente
      
      // Configurar sombra do texto
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      ctx.shadowBlur = 4;
      
      // Quebrar texto em linhas
      const lines = movieTitle.toUpperCase().split('\n');
      const safeMargin = canvas.width * 0.0625; // 6.25% das laterais
      const maxWidth = canvas.width - safeMargin * 2;
      const processedLines = [];
      
      // Calcular tamanho da fonte proporcional
      const scaledFontSize = fontSize * (canvas.width / 1920);
      ctx.font = `bold ${scaledFontSize}px ${getCurrentFontFamily()}`;
      
      // Processar cada linha para quebra automática se necessário
      lines.forEach(line => {
        const words = line.split(' ');
        let currentLine = '';
        
        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine !== '') {
            processedLines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          processedLines.push(currentLine);
        }
      });
      
      // Calcular posição do texto - usar APENAS os sliders
      const textX = canvas.width * (textPositionX / 100);
      const textY = canvas.height * (textPositionY / 100);
      
      // Desenhar as linhas
      const lineHeight = scaledFontSize * 1.2;
      const totalTextHeight = processedLines.length * lineHeight;
      let startY = textY;
      
      // Ajustar startY baseado no alinhamento vertical (apenas para referência visual)
      if (textAlignVertical === 'top') {
        startY = textY;
      } else if (textAlignVertical === 'middle') {
        startY = textY - totalTextHeight / 2;
      } else { // bottom
        startY = textY - totalTextHeight;
      }
      
      processedLines.forEach((line, index) => {
        const y = startY + index * lineHeight + scaledFontSize;
        ctx.fillText(line, textX, y);
      });
      
      // Limpar sombra
      ctx.shadowColor = 'transparent';
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowBlur = 0;
      
      // Adicionar marca d'água se existir
      if (watermarkImage) {
        const watermarkImg = new Image();
        watermarkImg.onload = () => {
          const watermarkSize = canvas.width * 0.0625; // 6.25% da largura
          const margin = canvas.width * 0.0208; // 2.08% da largura
          const x = canvas.width - watermarkSize - margin;
          const y = canvas.height * 0.037; // 3.7% da altura
          
          ctx.drawImage(watermarkImg, x, y, watermarkSize, watermarkSize);
          
          // Converter para blob
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            setPreviewBlob(url);
          }, 'image/png');
        };
        watermarkImg.src = watermarkImage;
      } else {
        // Converter para blob sem marca d'água
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setPreviewBlob(url);
        }, 'image/png');
      }
    };
    
    img.src = backgroundImage;
  };

  // Gerar preview sempre que algo mudar
  useEffect(() => {
    // Adicionar um pequeno delay quando a fonte muda para garantir que ela seja carregada
    const timeoutId = setTimeout(() => {
      generatePreviewBlob();
    }, selectedFont ? 100 : 0);

    return () => clearTimeout(timeoutId);
  }, [backgroundImage, movieTitle, fontSize, selectedFont, backgroundPosition, watermarkImage, textAlignHorizontal, textAlignVertical, textPositionX, textPositionY]);

  const downloadThumbnail = () => {
    if (!backgroundImage || !movieTitle) {
      alert('Por favor, adicione uma imagem e um título do filme.');
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Definir dimensões HD 16:9
    const width = 1920;
    const height = 1080;
    canvas.width = width;
    canvas.height = height;

    const img = new Image();
    img.onload = () => {
      // Desenhar imagem de fundo com posição ajustável
      const imgAspect = img.width / img.height;
      const canvasAspect = width / height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgAspect > canvasAspect) {
        // Imagem mais larga - ajustar altura
        drawHeight = height;
        drawWidth = drawHeight * imgAspect;
        drawX = (width - drawWidth) / 2;
        drawY = 0;
      } else {
        // Imagem mais alta - ajustar largura e posição vertical
        drawWidth = width;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        // Aplicar posição vertical
        const maxOffset = drawHeight - height;
        drawY = -(maxOffset * backgroundPosition / 100);
      }
      
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      
      // Criar gradiente para sombra na parte inferior
      const gradient = ctx.createLinearGradient(0, height * 0.5, 0, height);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
      
      // Aplicar sombra
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Configurar texto
      ctx.fillStyle = 'white';
      ctx.textAlign = textAlignHorizontal;
      ctx.textBaseline = 'alphabetic'; // Usar baseline consistente
      
      // Configurar sombra do texto
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      ctx.shadowBlur = 4;
      
      // Quebrar texto em linhas
      const lines = movieTitle.toUpperCase().split('\n');
      const safeMargin = 120; // margem de segurança
      const maxWidth = width - safeMargin * 2;
      const processedLines = [];
      
      // Usar o fontSize do slider
      ctx.font = `bold ${fontSize}px ${getCurrentFontFamily()}`;
      
      // Processar cada linha para quebra automática se necessário
      lines.forEach(line => {
        const words = line.split(' ');
        let currentLine = '';
        
        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine !== '') {
            processedLines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          processedLines.push(currentLine);
        }
      });
      
      // Calcular posição do texto - usar APENAS os sliders
      const textX = width * (textPositionX / 100);
      const textY = height * (textPositionY / 100);
      
      // Desenhar as linhas
      const lineHeight = fontSize * 1.2;
      const totalTextHeight = processedLines.length * lineHeight;
      let startY = textY;
      
      // Ajustar startY baseado no alinhamento vertical (apenas para referência visual)
      if (textAlignVertical === 'top') {
        startY = textY;
      } else if (textAlignVertical === 'middle') {
        startY = textY - totalTextHeight / 2;
      } else { // bottom
        startY = textY - totalTextHeight;
      }
      
      processedLines.forEach((line, index) => {
        const y = startY + index * lineHeight + fontSize;
        ctx.fillText(line, textX, y);
      });
      
      // Limpar sombra
      ctx.shadowColor = 'transparent';
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowBlur = 0;
      
      // Adicionar marca d'água se existir
      if (watermarkImage) {
        const watermarkImg = new Image();
        watermarkImg.onload = () => {
          const watermarkSize = 120;
          const margin = 40;
          const x = width - watermarkSize - margin;
          const y = margin;
          
          ctx.drawImage(watermarkImg, x, y, watermarkSize, watermarkSize);
          
          // Download da imagem
          const link = document.createElement('a');
          link.download = `${movieTitle.toLowerCase().replace(/\s+/g, '-')}-thumbnail.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        };
        watermarkImg.src = watermarkImage;
      } else {
        // Download da imagem sem marca d'água
        const link = document.createElement('a');
        link.download = `${movieTitle.toLowerCase().replace(/\s+/g, '-')}-thumbnail.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    };
    
    img.src = backgroundImage;
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">
          Criador de Thumbnails
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Coluna da Esquerda - Inputs */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Configurações</h2>
            
            {/* Upload de Imagem */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagem de Fundo
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                  Escolher Imagem
                </button>
                <p className="text-gray-500 text-sm mt-2">
                  Clique para fazer upload da imagem de fundo
                </p>
              </div>
            </div>

            {/* Campo de Texto */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Filme
              </label>
              <textarea
                value={movieTitle}
                onChange={(e) => setMovieTitle(e.target.value)}
                placeholder="Digite o nome do filme... (Use Enter para quebrar linha)"
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              />
            </div>

            {/* Upload de Marca d'Água */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marca d'Água (Opcional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  ref={watermarkInputRef}
                  onChange={handleWatermarkUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => watermarkInputRef.current.click()}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded text-sm"
                >
                  {watermarkImage ? 'Trocar Marca d\'Água' : 'Adicionar Marca d\'Água'}
                </button>
                {watermarkImage && (
                  <button
                    onClick={() => setWatermarkImage(null)}
                    className="ml-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded text-sm"
                  >
                    Remover
                  </button>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  Imagem quadrada com fundo transparente (PNG recomendado)
                </p>
              </div>
            </div>

            {/* Seletor de Fonte */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fonte
              </label>
              <select
                value={selectedFont}
                onChange={(e) => setSelectedFont(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {fontOptions.map((font) => (
                  <option key={font.name} value={font.name} style={{ fontFamily: font.family }}>
                    {font.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Controle de Tamanho da Fonte */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tamanho da Fonte: {fontSize}px
              </label>
              <input
                type="range"
                min="80"
                max="250"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>80px</span>
                <span>250px</span>
              </div>
            </div>

            {/* Controle de Posição da Imagem */}
            {backgroundImage && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posição Vertical da Imagem: {backgroundPosition}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={backgroundPosition}
                  onChange={(e) => setBackgroundPosition(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Topo</span>
                  <span>Centro</span>
                  <span>Base</span>
                </div>
              </div>
            )}

            {/* Controles de Alinhamento do Texto */}
            {backgroundImage && movieTitle && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Alinhamento do Texto
                </label>
                
                {/* Alinhamento Horizontal */}
                <div className="mb-4">
                  <div className="text-xs text-gray-600 mb-2">Horizontal</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setTextAlignHorizontal('left');
                        setTextPositionX(7); // 7% da esquerda
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignHorizontal === 'left'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ← Esquerda
                    </button>
                    <button
                      onClick={() => {
                        setTextAlignHorizontal('center');
                        setTextPositionX(50); // 50% do centro
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignHorizontal === 'center'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ↔ Centro
                    </button>
                    <button
                      onClick={() => {
                        setTextAlignHorizontal('right');
                        setTextPositionX(93); // 93% da direita
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignHorizontal === 'right'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Direita →
                    </button>
                  </div>
                </div>

                {/* Alinhamento Vertical */}
                <div className="mb-4">
                  <div className="text-xs text-gray-600 mb-2">Vertical</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setTextAlignVertical('top');
                        setTextPositionY(7); // 7% do topo
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignVertical === 'top'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ↑ Topo
                    </button>
                    <button
                      onClick={() => {
                        setTextAlignVertical('middle');
                        setTextPositionY(50); // 50% do meio
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignVertical === 'middle'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ↕ Meio
                    </button>
                    <button
                      onClick={() => {
                        setTextAlignVertical('bottom');
                        setTextPositionY(93); // 93% da base
                      }}
                      className={`flex-1 py-2 px-3 rounded text-sm font-medium ${
                        textAlignVertical === 'bottom'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ↓ Base
                    </button>
                  </div>
                </div>

                {/* Sliders de Posição */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Posição Horizontal: {textPositionX.toFixed(1)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={textPositionX}
                      onChange={(e) => setTextPositionX(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Posição Vertical: {textPositionY.toFixed(1)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={textPositionY}
                      onChange={(e) => setTextPositionY(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Botão de Download */}
            <div className="text-center">
              <button
                onClick={downloadThumbnail}
                disabled={!backgroundImage || !movieTitle}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg disabled:cursor-not-allowed"
              >
                Baixar Thumbnail
              </button>
            </div>
          </div>

          {/* Coluna da Direita - Preview */}
          <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8 self-start">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Preview</h2>
            
            {backgroundImage && movieTitle ? (
              <div
                ref={previewRef}
                className="relative w-full bg-gray-200 overflow-hidden"
                style={{ aspectRatio: '16/9' }}
              >
                {previewBlob ? (
                  <img
                    src={previewBlob}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-gray-500">Gerando preview...</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-lg">Adicione uma imagem e título para ver o preview</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Canvas oculto para geração da imagem */}
        <canvas ref={canvasRef} className="hidden"></canvas>
        <canvas ref={previewCanvasRef} className="hidden"></canvas>
      </div>
    </div>
  );
}

export default App;